//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// login-welcome imports
import * as s from "../LoginWrapper.styles";
import { loginWelcomeFragments } from "./LoginWelcome.gql";
// other internals
import { LoginBtn } from "#webapp/src/components/login-btn";
import { CookieDialog } from "@clockwise/web-commons/src/components/cookie-dialog";

// util
import { page, PageEvents, track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
// constants
import { paths } from "#webapp/src/constants/site.constant";
// state
import { loginUser } from "#webapp/src/mutations";
import { setOnboardingMeta } from "#webapp/src/state/actions/onboarding.actions";
import { hasSeenLoginPage } from "#webapp/src/state/local-storage";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";

// material-ui
import {
  OnboardingType,
  OnboardingTypeSource,
} from "#webapp/src/components/onboarding-nux/OnboardingNUXTypes";
import { LinearProgress } from "@material-ui/core";
import { CSSProperties } from "@material-ui/core/styles/withStyles";

// schema
import * as ISchema from "#webapp/src/__schema__";

import { useFeatureFlag } from "#webapp/src/launch-darkly/useLaunchDarkly";
import { Link } from "@clockwise/design-system";
import { getAnonymousId } from "@clockwise/web-commons/src/util/analytics.util";
import { signupExperimentInitialAnonymousId } from "@clockwise/web-commons/src/util/local-storage";
import { useRouter } from "found";
import Helmet from "react-helmet";
import { RelayProp } from "react-relay";
import { MsftLoginButton } from "../../msft-login-button";
import { isExtensionDownloadRedirect } from "./isExtensionDownloadRedirect";
import { isWelcomeSignupExperiment } from "./isWelcomeSignupExperiment";

interface IContainer {
  viewer: ISchema.IViewer;
  redirect?: string;
  ambiguous?: boolean;
  isNewUser?: boolean;
  onSuccess: () => void;
}

interface IProps extends IContainer {
  relay: RelayProp;
  isChromeExtension: boolean;
  setOnboardingMeta: typeof setOnboardingMeta;
  style?: CSSProperties;
}

//////////////////
// COMPONENT
//////////////////

const LoginWelcomeCmpt = ({
  redirect,
  isNewUser,
  isChromeExtension,
  onSuccess,
  setOnboardingMeta,
  viewer,
}: IProps) => {
  const { match } = useRouter();

  const [isOnM365BetaRelaunch] = useFeatureFlag("M365BetaRelaunch");

  const routeMatchesWelcomeExperiment = isWelcomeSignupExperiment(match);
  const routeMatchesChromeStoreDownload = isExtensionDownloadRedirect(match);

  const [loading, setLoading] = React.useState(false);
  const [hideCookieErrorDialog, setHideCookieErrorDialog] = React.useState(true);

  React.useEffect(() => {
    page(PageEvents.WELCOME, {
      redirect: redirect,
      isNewUser: isNewUser ? isNewUser : false,
    });
    hasSeenLoginPage.set(true);
    if (isChromeExtension) {
      PostMessageManager.openSidebar(true);
    }
    if (routeMatchesWelcomeExperiment) {
      signupExperimentInitialAnonymousId.set(getAnonymousId());
      track(TrackingEvents.Q4_2022_SIGNUP_EXPERIMENT.SIGNUP_EXPERIMENT_WELCOME_VISITED);
    }
    if (routeMatchesChromeStoreDownload) {
      track(TrackingEvents.CHROME_EXTENSION.INSTALL_REDIRECT_WELCOME);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setLoading(false);
  }, [viewer.user]);

  const enableWelcomeLoginOnboardingType = () => {
    setOnboardingMeta(OnboardingType.Default, OnboardingTypeSource.WelcomeLoginPage);
  };

  const onLoginStart = () => {
    track(TrackingEvents.AUTH.BEGIN_LOGIN);
    setLoading(true);
  };

  const onLoginSuccess = () => {
    enableWelcomeLoginOnboardingType();
    if (routeMatchesWelcomeExperiment) {
      const initialAnonymousId = signupExperimentInitialAnonymousId.get();
      track(TrackingEvents.Q4_2022_SIGNUP_EXPERIMENT.SIGNUP_EXPERIMENT_WELCOME_LOGIN, {
        initialAnonymousId: initialAnonymousId,
      });
    }
    onSuccess();
  };

  const onLoginFailure = () => {
    track(TrackingEvents.AUTH.LOGIN_FAILED);
    setLoading(false);
  };

  const renderCookieDialog = () => {
    return <CookieDialog isChromeExtension={isChromeExtension} open={!hideCookieErrorDialog} />;
  };

  const onGoogleFailure = (e: any) => {
    if (e.error === "idpiframe_initialization_failed") {
      setHideCookieErrorDialog(false);
    }
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Welcome to Clockwise · Clockwise</title>
      </Helmet>
      {renderCookieDialog()}
      <div className="cw-w-full cw-h-full cw-bg-default cw-flex cw-items-center cw-justify-center">
        <div className="cw-flex cw-flex-col cw-items-center cw-mt-[-50px]">
          <div className="cw-text-center cw-heading-3xl cw-mb-5">Welcome to Clockwise!</div>
          <div className="cw-w-[215px] cw-mb-4">
            <LoginBtn
              label={"Sign in with Google"}
              loginUserMutation={loginUser}
              style={s.signupBtn.base}
              labelStyle={s.signupBtn.label}
              redirect={redirect}
              onStart={onLoginStart}
              onSuccess={onLoginSuccess}
              onFailure={onLoginFailure}
              onGoogleFailure={onGoogleFailure}
            />
          </div>
          {isOnM365BetaRelaunch ? (
            <div className="cw-w-[300px] cw-mb-4 cw-flex cw-items-center cw-w-5">
              <div className="cw-grow cw-border-b cw-border-solid cw-border-muted"></div>
              <div className="cw-px-2 cw-caption cw-text-muted cw-text-center">
                or try out our latest beta
              </div>
              <div className="cw-grow cw-border-b cw-border-solid cw-border-muted"></div>
            </div>
          ) : (
            ""
          )}
          <div className="cw-w-[215px] cw-mb-4">
            <MsftLoginButton redirect={redirect} />
          </div>
          <div className="cw-caption cw-text-center cw-text-muted cw-w-[250px]">
            By signing in, you agree to Clockwise's{" "}
            <Link target="_blank" href={paths.terms}>
              {" "}
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link target="_blank" href={paths.privacy}>
              Privacy Policy
            </Link>
            .
          </div>

          {loading && (
            <LinearProgress
              className="cw-bg-transparent cw-absolute cw-bottom-0 cw-w-full"
              variant="indeterminate"
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    isChromeExtension: state.webExtension.isWebExtension,
  };
}

const reduxContainer = connect(mapStateToProps, {
  setOnboardingMeta,
})(LoginWelcomeCmpt);

//////////////////
// RELAY
//////////////////
export const LoginWelcome = createFragmentContainer<IContainer>(
  reduxContainer,
  loginWelcomeFragments,
);
